import React from "react";
import { useTranslation } from "react-i18next";
import { Users, MapPin } from "lucide-react";

export default function TableMap({ tables, selectedTableId, onSelect, partySize }) {
    const { t } = useTranslation();
    if (!tables || tables.length === 0) {
        return (
            <div className="bg-ink-surface border border-white/10 rounded-sm p-8 text-center" data-testid="table-map-empty">
                <p className="text-gray-400 font-serif text-lg">{t("table_map.empty")}</p>
            </div>
        );
    }

    return (
        <div className="bg-ink-surface border border-white/10 rounded-sm p-6" data-testid="table-map">
            <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif text-2xl text-white">{t("table_map.title")}</h3>
                <div className="flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-gold rounded-full" />
                        <span className="text-gray-400">{t("table_map.free")}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-zinc-700 rounded-full" />
                        <span className="text-gray-400">{t("table_map.occupied")}</span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {tables.map((tbl) => {
                    const isSelected = tbl.table_id === selectedTableId;
                    const tooSmall = partySize && tbl.seats < partySize;
                    const disabled = !tbl.available || tooSmall;

                    return (
                        <button
                            key={tbl.table_id}
                            type="button"
                            onClick={() => !disabled && onSelect(tbl)}
                            disabled={disabled}
                            data-testid={`table-${tbl.number}`}
                            className={`
                                relative p-4 border rounded-sm text-left transition-all duration-300
                                ${isSelected
                                    ? "border-gold bg-gold/10 shadow-[0_0_20px_rgba(212,175,55,0.3)]"
                                    : tbl.available && !tooSmall
                                        ? "border-gold/40 bg-ink hover:border-gold hover:bg-gold/5 cursor-pointer"
                                        : "border-white/10 bg-zinc-900/40 opacity-50 cursor-not-allowed"}
                            `}
                        >
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="text-xs text-gray-400 uppercase tracking-widest">{t("table_map.table")}</div>
                                    <div className={`text-3xl font-serif ${isSelected || (tbl.available && !tooSmall) ? "text-gold" : "text-zinc-600"}`}>
                                        {tbl.number}
                                    </div>
                                </div>
                                <MapPin size={14} className={tbl.location === "outdoor" ? "text-emerald-500/60" : "text-gray-500"} strokeWidth={1.5} />
                            </div>
                            <div className="mt-3 flex items-center gap-1.5 text-xs text-gray-400">
                                <Users size={12} strokeWidth={1.5} />
                                <span>{t("table_map.seats", { count: tbl.seats })}</span>
                            </div>
                            <div className="text-[10px] text-gray-500 uppercase tracking-wider mt-1">
                                {tbl.location === "outdoor" ? t("table_map.outdoor") : t("table_map.indoor")}
                            </div>
                            {tooSmall && tbl.available && (
                                <div className="absolute top-2 right-2 text-[9px] bg-red-500/20 text-red-400 px-2 py-0.5 rounded-sm">
                                    {t("table_map.few_seats")}
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
